<template>
  <div class="min-h-screen">
    <AppHeader />
    
    <!-- Loading State -->
    <div v-if="loading" class="section-padding">
      <div class="container-custom">
        <SkeletonLoader type="title" width="400px" class="mx-auto mb-4" />
        <SkeletonLoader type="text" width="600px" class="mx-auto mb-8" />
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <SkeletonLoader v-for="i in 3" :key="i" type="card" height="400px" />
        </div>
      </div>
    </div>

    <!-- Portfolio Content -->
    <div v-else-if="portfolio">
      <!-- Hero Section with Featured Image -->
      <!-- Hero Section with Immersive Background -->
      <section class="relative overflow-hidden section-padding min-h-[60vh] flex items-center">
        <!-- Background Image & Overlay -->
        <div class="absolute inset-0 z-0">
          <img 
            v-if="portfolio.featured_image_url"
            :src="portfolio.featured_image_url" 
            :alt="portfolio.title"
            class="w-full h-full object-cover"
          />
          <div v-else class="w-full h-full bg-gray-900"></div>
          <!-- Gradient Overlay -->
          <div class="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/60"></div>
        </div>
        
        <div class="container-custom relative z-10">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <!-- Left Column: Content -->
            <div class="space-y-8">
              <!-- Category Badge -->
              <div class="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm font-medium shadow-lg">
                <svg class="w-4 h-4 mr-2 text-primary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
                </svg>
                {{ portfolio.category }}
              </div>

              <!-- Featured Badge -->
              <div v-if="portfolio.featured" class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-400/20 backdrop-blur-md border border-yellow-400/30 text-yellow-300 text-sm font-bold shadow-lg ml-2">
                <svg class="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/></svg>
                Featured Project
              </div>

              <!-- Title -->
              <div class="space-y-6">
                <h1 class="font-heading text-4xl lg:text-6xl font-bold leading-tight text-white">
                  {{ portfolio.title }}
                </h1>
              </div>

              <!-- Client Name -->
              <div class="flex items-center gap-2 text-gray-300">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
                <span class="text-lg">Client: <strong class="text-white">{{ portfolio.client_name }}</strong></span>
              </div>

              <!-- Short Description -->
              <p class="text-xl text-gray-300 leading-relaxed max-w-xl">
                {{ portfolio.description }}
              </p>

              <!-- Key Info Pills (Glassmorphism) -->
              <div class="flex flex-wrap gap-4">
                <div v-if="portfolio.timeline_weeks" class="px-6 py-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/10 shadow-lg hover:bg-white/20 transition-all">
                  <div class="text-sm text-gray-300 mb-1">Timeline</div>
                  <div class="text-2xl font-bold text-white">{{ portfolio.timeline_weeks }} weeks</div>
                </div>
                <div v-if="portfolio.team_size" class="px-6 py-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/10 shadow-lg hover:bg-white/20 transition-all">
                  <div class="text-sm text-gray-300 mb-1">Team Size</div>
                  <div class="text-2xl font-bold text-white">{{ portfolio.team_size }} members</div>
                </div>
                <div v-if="portfolio.completed_at" class="px-6 py-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/10 shadow-lg hover:bg-white/20 transition-all">
                  <div class="text-sm text-gray-300 mb-1">Completed</div>
                  <div class="text-2xl font-bold text-white">{{ formatYear(portfolio.completed_at) }}</div>
                </div>
              </div>

              <!-- Project Links -->
              <div v-if="portfolio.project_url || portfolio.github_url" class="flex flex-wrap gap-4">
                <a v-if="portfolio.project_url" :href="portfolio.project_url" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                  </svg>
                  View Live Project
                </a>
                <a v-if="portfolio.github_url" :href="portfolio.github_url" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-md border border-white/20 text-white rounded-xl font-semibold hover:bg-white/20 transition-colors shadow-sm">
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                  View on GitHub
                </a>
              </div>
            </div>

            <!-- Right Column: Featured Image in Glass Frame -->
            <div class="relative group perspective-1000 hidden lg:block">
              <!-- Image Container -->
              <div class="relative rounded-3xl overflow-hidden shadow-2xl border border-white/20 bg-white/5 backdrop-blur-sm transform transition-all duration-500 group-hover:rotate-y-2 group-hover:scale-[1.02] p-2">
                <div class="rounded-2xl overflow-hidden">
                  <img 
                    v-if="portfolio.featured_image_url"
                    :src="portfolio.featured_image_url" 
                    :alt="portfolio.title"
                    class="w-full h-auto object-cover min-h-[400px]"
                  />
                  <div v-else class="w-full h-[400px] bg-gray-800 flex items-center justify-center">
                    <svg class="w-24 h-24 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                  </div>
                </div>
              </div>

              <!-- Decorative Elements around image -->
              <div class="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full blur-2xl opacity-40 -z-10"></div>
              <div class="absolute -bottom-8 -left-8 w-32 h-32 bg-gradient-to-tr from-secondary-500 to-primary-500 rounded-full blur-2xl opacity-40 -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      <!-- Portfolio Overview Section -->
      <section class="section-padding bg-white">
        <div class="container-custom">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <!-- Long Description -->
            <div>
              <h2 class="font-heading text-3xl font-bold text-gray-900 mb-6">
                Project Overview
              </h2>
              <div class="prose prose-lg max-w-none text-gray-600 leading-relaxed">
                {{ portfolio.long_description || portfolio.description }}
              </div>
            </div>

            <!-- Technologies, Features & Results -->
            <div class="space-y-8">
              <!-- Technologies -->
              <div v-if="portfolio.technologies && portfolio.technologies.length > 0">
                <h3 class="font-heading text-2xl font-bold text-gray-900 mb-4">
                  Technologies Used
                </h3>
                <div class="flex flex-wrap gap-2">
                  <span 
                    v-for="(tech, index) in portfolio.technologies" 
                    :key="index"
                    class="px-4 py-2 bg-gradient-to-r from-primary-50 to-secondary-50 text-primary-700 rounded-full text-sm font-medium border border-primary-200 hover:shadow-md transition-shadow"
                  >
                    {{ tech }}
                  </span>
                </div>
              </div>

              <!-- Key Features -->
              <div v-if="portfolio.features && portfolio.features.length > 0">
                <h3 class="font-heading text-2xl font-bold text-gray-900 mb-4">
                  Key Features
                </h3>
                <ul class="space-y-3">
                  <li 
                    v-for="(feature, index) in portfolio.features" 
                    :key="index"
                    class="flex items-start group"
                  >
                    <div class="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center mt-0.5 group-hover:bg-primary-200 transition-colors">
                      <svg class="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                    <span class="ml-3 text-gray-700">{{ feature }}</span>
                  </li>
                </ul>
              </div>

              <!-- Results -->
              <div v-if="portfolio.results && portfolio.results.length > 0">
                <h3 class="font-heading text-2xl font-bold text-gray-900 mb-4">
                  Project Results
                </h3>
                <ul class="space-y-3">
                  <li 
                    v-for="(result, index) in portfolio.results" 
                    :key="index"
                    class="flex items-start group"
                  >
                    <div class="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5 group-hover:bg-green-200 transition-colors">
                      <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                    </div>
                    <span class="ml-3 text-gray-700">{{ result }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Screenshots Gallery Section -->
      <section v-if="portfolio.screenshots && portfolio.screenshots.length > 0" class="section-padding bg-gradient-to-br from-gray-50 to-primary-50/30">
        <div class="container-custom">
          <div class="text-center mb-12">
            <div class="inline-flex items-center px-4 py-2 rounded-full bg-white border border-primary-100 text-primary-600 text-sm font-medium mb-6 shadow-lg">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
              Project Gallery
            </div>
            <h2 class="font-heading text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Screenshots & Visuals
            </h2>
            <p class="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore the visual journey of this project
            </p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div 
              v-for="screenshot in portfolio.screenshots" 
              :key="screenshot.id"
              class="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
              @click="openLightbox(screenshot)"
            >
              <img 
                :src="screenshot.image_url" 
                :alt="screenshot.caption || portfolio.title"
                class="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div v-if="screenshot.caption" class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p class="text-white text-sm font-medium">{{ screenshot.caption }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Related Services Section -->
      <section v-if="relatedServices.length > 0" class="section-padding bg-white">
        <div class="container-custom">
          <div class="text-center mb-12">
            <div class="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-primary-50 to-secondary-50 border border-primary-100 text-primary-600 text-sm font-medium mb-6 shadow-lg">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
              </svg>
              Services Used
            </div>
            <h2 class="font-heading text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Services Utilized in This Project
            </h2>
            <p class="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover the services that made this project successful
            </p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <router-link
              v-for="service in relatedServices"
              :key="service.id"
              :to="{ name: 'ServiceDetail', params: { id: service.uid } }"
              class="group card overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <!-- Service Banner -->
              <div class="relative h-48 bg-gradient-to-br from-primary-100 to-secondary-100 overflow-hidden">
                <img 
                  v-if="service.image"
                  :src="getServiceBannerUrl(service.image)" 
                  :alt="service.name"
                  class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>

              <!-- Service Info -->
              <div class="p-6">
                <h3 class="font-heading text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {{ service.name }}
                </h3>
                <p class="text-gray-600 mb-4 line-clamp-2">
                  {{ service.short_description }}
                </p>
                
                <div class="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div>
                    <p class="text-xs text-gray-500 mb-1">Starting from</p>
                    <p class="text-lg font-bold text-primary-600">${{ service.starting_price }}</p>
                  </div>
                  <div class="text-right">
                    <p class="text-xs text-gray-500 mb-1">Timeline</p>
                    <p class="text-sm font-semibold text-gray-700">{{ service.timeline_min_weeks }}-{{ service.timeline_max_weeks }} weeks</p>
                  </div>
                </div>
              </div>
            </router-link>
          </div>
        </div>
      </section>

      <!-- Related Portfolios Section -->
      <section v-if="relatedPortfolios.length > 0" class="section-padding bg-gradient-to-br from-gray-50 via-primary-50/30 to-secondary-50/30">
        <div class="container-custom">
          <div class="text-center mb-12">
            <div class="inline-flex items-center px-4 py-2 rounded-full bg-white border border-primary-100 text-primary-600 text-sm font-medium mb-6 shadow-lg">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
              </svg>
              More Projects
            </div>
            <h2 class="font-heading text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Other Projects You May Like
            </h2>
            <p class="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore more of our successful projects
            </p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <router-link
              v-for="relatedPortfolio in relatedPortfolios"
              :key="relatedPortfolio.id"
              :to="{ name: 'PortfolioDetail', params: { id: relatedPortfolio.uid } }"
              class="group card overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <!-- Portfolio Image -->
              <div class="relative h-56 bg-gradient-to-br from-primary-100 to-secondary-100 overflow-hidden">
                <img 
                  v-if="relatedPortfolio.featured_image_url"
                  :src="relatedPortfolio.featured_image_url" 
                  :alt="relatedPortfolio.title"
                  class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div v-else class="w-full h-full flex items-center justify-center">
                  <svg class="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                </div>
                <div class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <!-- Featured Badge -->
                <div v-if="relatedPortfolio.featured" class="absolute top-3 right-3">
                  <span class="flex items-center gap-1 px-3 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full shadow-lg">
                    <svg class="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/></svg>
                    Featured
                  </span>
                </div>

                <!-- View Details Button (appears on hover) -->
                <div class="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button class="px-6 py-3 bg-white text-primary-600 font-semibold rounded-lg shadow-lg hover:bg-primary-50 transition-colors">
                    View Details
                  </button>
                </div>
              </div>

              <div class="p-6">
                <div class="flex items-center justify-between mb-3">
                  <span class="text-sm font-semibold text-primary-600 bg-primary-50 px-3 py-1 rounded-full">
                    {{ relatedPortfolio.category }}
                  </span>
                  <span v-if="relatedPortfolio.completed_at" class="text-sm text-gray-500">
                    {{ formatYear(relatedPortfolio.completed_at) }}
                  </span>
                </div>
                
                <h3 class="font-heading text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                  {{ relatedPortfolio.title }}
                </h3>
                
                <p class="text-gray-600 text-sm mb-4 line-clamp-2">
                  {{ relatedPortfolio.description }}
                </p>

                <!-- Technologies -->
                <div v-if="relatedPortfolio.technologies && relatedPortfolio.technologies.length > 0" class="flex flex-wrap gap-2">
                  <span
                    v-for="tech in relatedPortfolio.technologies.slice(0, 3)"
                    :key="tech"
                    class="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded font-medium"
                  >
                    {{ tech }}
                  </span>
                  <span v-if="relatedPortfolio.technologies.length > 3" class="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded">
                    +{{ relatedPortfolio.technologies.length - 3 }}
                  </span>
                </div>
              </div>
            </router-link>
          </div>
        </div>
      </section>

      <!-- CTA Section -->
      <section class="relative overflow-hidden bg-gray-50 section-padding">
        <!-- Background Elements -->
        <div class="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div class="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary-200/40 blur-3xl"></div>
          <div class="absolute -bottom-[20%] -left-[10%] w-[40%] h-[40%] rounded-full bg-secondary-200/40 blur-3xl"></div>
        </div>

        <div class="container-custom text-center relative z-10">
          <div class="max-w-4xl mx-auto">
            <div class="inline-flex items-center px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-primary-100 text-primary-600 text-sm font-medium mb-6 shadow-lg">
              <span class="flex h-2 w-2 rounded-full bg-primary-600 mr-2 animate-pulse"></span>
              Start Your Project
            </div>
            <h2 class="font-heading text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Ready to Create Something Amazing?
            </h2>
            <p class="text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              Let's work together to bring your vision to life, just like we did for {{ portfolio.client_name }}.
            </p>
            <div class="flex flex-wrap gap-4 justify-center">
              <router-link 
                to="/contact" 
                class="btn btn-primary text-lg px-8 py-4 shadow-xl shadow-primary-500/20 hover:shadow-primary-500/40"
              >
                Contact Us
              </router-link>
              <router-link 
                to="/portfolio" 
                class="btn bg-white border-2 border-gray-300 text-gray-900 hover:border-gray-400 text-lg px-8 py-4"
              >
                View All Projects
              </router-link>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Error State -->
    <div v-else class="section-padding">
      <div class="container-custom text-center">
        <div class="max-w-md mx-auto">
          <svg class="w-24 h-24 mx-auto text-gray-400 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <h2 class="font-heading text-3xl font-bold text-gray-900 mb-4">Portfolio Not Found</h2>
          <p class="text-gray-600 mb-8">The portfolio project you're looking for doesn't exist or has been removed.</p>
          <router-link to="/portfolio" class="btn btn-primary">
            View All Projects
          </router-link>
        </div>
      </div>
    </div>
    
    <AppFooter />

    <!-- Enhanced Screenshot Lightbox -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 pb-10"
        enter-to-class="opacity-100 pb-0"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div v-if="lightboxImage" class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/95 backdrop-blur-sm">
          <!-- Close Button -->
          <button @click="closeLightbox" class="absolute top-6 right-6 z-[120] p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors">
            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>

          <!-- Navigation Controls -->
          <div class="absolute inset-y-0 left-0 flex items-center p-4 z-[110]">
            <button @click="prevScreenshot" class="p-4 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
              </svg>
            </button>
          </div>
          <div class="absolute inset-y-0 right-0 flex items-center p-4 z-[110]">
            <button @click="nextScreenshot" class="p-4 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </button>
          </div>

          <!-- Zoom Controls -->
          <div class="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-white/10 backdrop-blur-md rounded-full px-6 py-3 z-[110]">
            <button @click="zoomOut" class="text-white hover:text-primary-400 p-1">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"></path>
              </svg>
            </button>
            <span class="text-white font-medium min-w-[3rem] text-center">{{ Math.round(zoomLevel * 100) }}%</span>
            <button @click="zoomIn" class="text-white hover:text-primary-400 p-1">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"></path>
              </svg>
            </button>
            <button @click="resetZoom" class="text-white hover:text-primary-400 p-1 ml-2 border-l border-white/20 pl-4">
              Reset
            </button>
          </div>

          <!-- Image Container -->
          <div class="relative w-full h-full flex items-center justify-center overflow-auto scrollbar-hide" @click.self="closeLightbox">
            <img 
              :src="lightboxImage" 
              class="max-w-none transition-transform duration-200 cursor-grab active:cursor-grabbing"
              :style="{ transform: `scale(${zoomLevel})` }"
              @click.stop
            />
          </div>

          <!-- Caption -->
          <div class="absolute bottom-24 left-1/2 -translate-x-1/2 text-center">
            <p v-if="currentScreenshot?.caption" class="text-white text-lg font-medium drop-shadow-lg">{{ currentScreenshot.caption }}</p>
            <p class="text-white/60 text-sm mt-1">{{ currentScreenshotIndex + 1 }} / {{ portfolio.screenshots.length }}</p>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { usePortfolioStore } from '@/stores/portfolio'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'
import SkeletonLoader from '@/components/SkeletonLoader.vue'

const route = useRoute()
const router = useRouter()
const portfolioStore = usePortfolioStore()

const loading = ref(true)
const portfolio = ref(null)
const lightboxImage = ref(null)
const currentScreenshotIndex = ref(0)
const zoomLevel = ref(1)

const currentScreenshot = computed(() => {
  if (!portfolio.value || !portfolio.value.screenshots) return null
  return portfolio.value.screenshots[currentScreenshotIndex.value]
})

onMounted(async () => {
  await loadPortfolioDetails()
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
})

// Watch for route changes (when user clicks on another portfolio)
watch(() => route.params.id, async (newId) => {
  if (newId) {
    await loadPortfolioDetails()
  }
})

const loadPortfolioDetails = async () => {
  loading.value = true
  try {
    const id = route.params.id
    
    // Fetch portfolio details with services relationship
    const portfolioData = await portfolioStore.fetchPortfolioItem(id)
    
    if (portfolioData) {
      portfolio.value = portfolioData
      
      // Fetch all portfolios if needed for related portfolios section
      if (!portfolioStore.portfolioItems || portfolioStore.portfolioItems.length === 0) {
        await portfolioStore.fetchPortfolio()
      }
    } else {
      portfolio.value = null
    }
  } catch (error) {
    console.error('Error loading portfolio details:', error)
    portfolio.value = null
  } finally {
    loading.value = false
  }
}

// Computed: Related services used in this portfolio
const relatedServices = computed(() => {
  if (!portfolio.value || !portfolio.value.services) return []
  return portfolio.value.services
})

// Computed: Other portfolios (excluding current)
const relatedPortfolios = computed(() => {
  if (!portfolio.value) return []
  
  return portfolioStore.portfolioItems
    .filter(p => p.id !== portfolio.value.id)
    .slice(0, 6) // Limit to 6 portfolios
})

const formatYear = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.getFullYear()
}

const openLightbox = (screenshot) => {
  const index = portfolio.value.screenshots.findIndex(s => s.id === screenshot.id)
  if (index !== -1) {
    currentScreenshotIndex.value = index
    lightboxImage.value = screenshot.image_url
    zoomLevel.value = 1
    document.body.style.overflow = 'hidden'
  }
}

const closeLightbox = () => {
  lightboxImage.value = null
  zoomLevel.value = 1
  document.body.style.overflow = ''
}

const nextScreenshot = () => {
  if (!portfolio.value || !portfolio.value.screenshots) return
  currentScreenshotIndex.value = (currentScreenshotIndex.value + 1) % portfolio.value.screenshots.length
  lightboxImage.value = portfolio.value.screenshots[currentScreenshotIndex.value].image_url
  zoomLevel.value = 1
}

const prevScreenshot = () => {
  if (!portfolio.value || !portfolio.value.screenshots) return
  currentScreenshotIndex.value = (currentScreenshotIndex.value - 1 + portfolio.value.screenshots.length) % portfolio.value.screenshots.length
  lightboxImage.value = portfolio.value.screenshots[currentScreenshotIndex.value].image_url
  zoomLevel.value = 1
}

const zoomIn = () => {
  if (zoomLevel.value < 4) zoomLevel.value += 0.25
}

const zoomOut = () => {
  if (zoomLevel.value > 0.5) zoomLevel.value -= 0.25
}

const resetZoom = () => {
  zoomLevel.value = 1
}

const handleKeydown = (e) => {
  if (!lightboxImage.value) return
  
  if (e.key === 'Escape') closeLightbox()
  if (e.key === 'ArrowRight') nextScreenshot()
  if (e.key === 'ArrowLeft') prevScreenshot()
  if (e.key === '+' || e.key === '=') zoomIn()
  if (e.key === '-' || e.key === '_') zoomOut()
}

const getServiceBannerUrl = (imagePath) => {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'
  const baseUrl = API_BASE_URL.replace('/api/v1', '')
  return `${baseUrl}/storage/${imagePath}`
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.prose {
  max-width: none;
}
</style>
